//full list of Firebase Auth error codes https://firebase.google.com/docs/reference/js/auth#autherrorcodes

export enum AuthErrorCode {
 INVALID_PASSWORD = "auth/wrong-password",
 USER_NOT_FOUND = "auth/user-not-found",
 EMAIL_EXISTS = "auth/email-already-in-use",
 ACCOUNT_EXISTS_WITH_DIFFERENT_PROVIDER = "auth/account-exists-with-different-credential"
}

/**
 * Sign-in failures caused by the person signing in or by their network rather than by the app:
 * a wrong password, a dismissed Google popup, an offline laptop. Each gets a message in the UI
 * and stays below elmah.io's console.error capture. Anything outside this set — for example
 * auth/internal-error, auth/unauthorized-domain or auth/operation-not-allowed — points at a
 * misconfiguration or an outage, so it must report instead of being silently swallowed.
 */
const EXPECTED_SIGN_IN_FAILURE_CODES: ReadonlySet<string> = new Set<string>([
  AuthErrorCode.INVALID_PASSWORD,
  AuthErrorCode.USER_NOT_FOUND,
  AuthErrorCode.ACCOUNT_EXISTS_WITH_DIFFERENT_PROVIDER,
  // Firebase returns this in place of wrong-password/user-not-found once email enumeration
  // protection is enabled, so it has to be treated as the same expected outcome.
  "auth/invalid-credential",
  "auth/invalid-email",
  "auth/user-disabled",
  // The person dismissed or blocked the Google/Microsoft popup.
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/popup-blocked",
  "auth/user-cancelled",
  // Offline or throttled, not an application failure.
  "auth/network-request-failed",
  "auth/too-many-requests",
]);

/** True when a sign-in failure is an expected outcome rather than something worth reporting. */
export function isExpectedSignInFailure(code: string | undefined | null): boolean {
  return !!code && EXPECTED_SIGN_IN_FAILURE_CODES.has(code);
}